import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  LayoutDashboard, 
  FileText, 
  Users, 
  Settings, 
  LogOut, 
  Menu, 
  X,
  ChevronRight,
  Home,
  ClipboardList,
  Database,
  Shield
} from 'lucide-react';
import { useState } from 'react';
import { Button } from '../components/ui/button';

const LOGO_URL = "https://kroytechnologygroup.com/wp-content/uploads/2022/01/KTG-Final-Logo-Flat-300x200.png";

export const Navbar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <nav className="bg-white border-b border-slate-200 sticky top-0 z-40 backdrop-blur-md bg-white/90">
      <div className="page-container">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3">
            <img src={LOGO_URL} alt="AuditGuard" className="h-10 w-auto rounded-lg" />
            <span className="font-semibold text-slate-900 hidden sm:block" style={{ fontFamily: 'Outfit, sans-serif' }}>
              AuditGuard
            </span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-6">
            {user ? (
              <>
                <Link to={user.is_admin ? "/admin" : "/dashboard"} className="nav-link">
                  Dashboard
                </Link>
                {!user.is_admin && (
                  <>
                    <Link to="/templates" className="nav-link">
                      Templates
                    </Link>
                    <Link to="/team" className="nav-link">
                      Team
                    </Link>
                    <Link to="/assessments" className="nav-link" data-testid="nav-assessments">
                      Assessments
                    </Link>
                    <Link to="/profile" className="nav-link">
                      Profile
                    </Link>
                  </>
                )}
                <div className="flex items-center gap-4 ml-4 pl-4 border-l border-slate-200">
                  <span className="text-sm text-slate-600">
                    {user.company_name}
                  </span>
                  <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="logout-btn">
                    <LogOut className="h-4 w-4 mr-2" />
                    Logout
                  </Button>
                </div>
              </>
            ) : (
              <>
                <Link to="/login" className="nav-link">Login</Link>
                <Link to="/login">
                  <Button className="btn-primary px-4 py-2" data-testid="get-started-nav-btn">
                    Get Started
                  </Button>
                </Link>
              </>
            )}
          </div>

          {/* Mobile menu button */}
          <button
            className="md:hidden p-2 rounded-lg hover:bg-slate-100"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-btn"
          >
            {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-slate-200 animate-fade-in">
            {user ? (
              <div className="space-y-2">
                <Link 
                  to={user.is_admin ? "/admin" : "/dashboard"} 
                  className="block px-4 py-2 rounded-lg hover:bg-slate-100"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Dashboard
                </Link>
                {!user.is_admin && (
                  <>
                    <Link 
                      to="/templates" 
                      className="block px-4 py-2 rounded-lg hover:bg-slate-100"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      Templates
                    </Link>
                    <Link 
                      to="/team" 
                      className="block px-4 py-2 rounded-lg hover:bg-slate-100"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      Team
                    </Link>
                    <Link 
                      to="/assessments" 
                      className="block px-4 py-2 rounded-lg hover:bg-slate-100"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      Assessments
                    </Link>
                  </>
                )}
                <button 
                  onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
                  className="w-full text-left px-4 py-2 rounded-lg hover:bg-slate-100 text-red-600"
                >
                  Logout
                </button>
              </div>
            ) : (
              <div className="space-y-2">
                <Link 
                  to="/login" 
                  className="block px-4 py-2 rounded-lg hover:bg-slate-100"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Login
                </Link>
                <Link 
                  to="/login" 
                  className="block px-4 py-2 rounded-lg bg-[#FF6B35] text-white text-center"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Get Started
                </Link>
              </div>
            )}
          </div>
        )}
      </div>
    </nav>
  );
};

export const AdminSidebar = () => {
  const location = useLocation();
  const { logout } = useAuth();
  const navigate = useNavigate();

  const navItems = [
    { path: '/admin', icon: LayoutDashboard, label: 'Overview' },
    { path: '/admin/submissions', icon: ClipboardList, label: 'Submissions' },
    { path: '/admin/users', icon: Users, label: 'Users' },
    { path: '/admin/templates', icon: FileText, label: 'Templates' },
    { path: '/admin/settings', icon: Settings, label: 'Settings' },
    { path: '/admin/database', icon: Database, label: 'Database' },
  ];

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <aside className="w-64 bg-white border-r border-slate-200 min-h-screen flex flex-col">
      {/* Logo */}
      <div className="p-6 border-b border-slate-200">
        <Link to="/admin" className="flex items-center gap-3">
          <img src={LOGO_URL} alt="Kroy" className="h-10 w-auto rounded-lg" />
          <div>
            <span className="font-semibold text-slate-900 block" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Kroy Tech
            </span>
            <span className="text-xs text-slate-500">Admin Portal</span>
          </div>
        </Link>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4 space-y-1">
        {navItems.map(({ path, icon: Icon, label }) => (
          <Link
            key={path}
            to={path}
            className={`sidebar-nav-item ${location.pathname === path ? 'active' : ''}`}
            data-testid={`admin-nav-${label.toLowerCase()}`}
          >
            <Icon className="h-5 w-5" />
            <span>{label}</span>
          </Link>
        ))}
      </nav>

      {/* Footer */}
      <div className="p-4 border-t border-slate-200">
        <Link to="/" className="sidebar-nav-item mb-2">
          <Home className="h-5 w-5" />
          <span>Back to Site</span>
        </Link>
        <button onClick={handleLogout} className="sidebar-nav-item w-full text-red-600 hover:bg-red-50">
          <LogOut className="h-5 w-5" />
          <span>Logout</span>
        </button>
      </div>
    </aside>
  );
};

export const PageHeader = ({ title, description, breadcrumbs = [], actions }) => {
  return (
    <div className="mb-8">
      {/* Breadcrumbs */}
      {breadcrumbs.length > 0 && (
        <nav className="flex items-center gap-2 text-sm text-slate-500 mb-4">
          {breadcrumbs.map((crumb, index) => (
            <span key={index} className="flex items-center gap-2">
              {index > 0 && <ChevronRight className="h-4 w-4" />}
              {crumb.href ? (
                <Link to={crumb.href} className="hover:text-[#FF6B35] transition-colors">
                  {crumb.label}
                </Link>
              ) : (
                <span className="text-slate-900">{crumb.label}</span>
              )}
            </span>
          ))}
        </nav>
      )}

      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {title}
          </h1>
          {description && (
            <p className="mt-2 text-slate-600">{description}</p>
          )}
        </div>
        {actions && <div className="flex items-center gap-3">{actions}</div>}
      </div>
    </div>
  );
};

export const ClientLayout = ({ children }) => {
  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <main>{children}</main>
    </div>
  );
};

export const AdminLayout = ({ children }) => {
  return (
    <div className="min-h-screen bg-slate-50 flex">
      <AdminSidebar />
      <main className="flex-1 overflow-auto">{children}</main>
    </div>
  );
};
